<i18n>
{
	"en": {
		"menu-title-program": "Program: {name}",
    "program-settings": "Program Settings",
		"manage-organization-managers": "User Management",
		"manage-project-managers": "User Management",
    "manage-marketgroup-managers": "User Management",
    "manage-project-export-all-participants": "Export All Participants",
    "reconciliation-report": "Reconciliation Report",
    "subscription-end-report": "Subscription Report",
    "budget-allowance-report": "Budget Envelope Report",
    "cash-register": "Cash Registers",
    "menu-title-market": "Merchant: {name}",
    "menu-title-market-group": "Merchant Group: {name}"
	},
	"fr": {
		"menu-title-program": "Programme : {name}",
    "program-settings": "Paramètres du programme",
		"manage-organization-managers": "Gestion des comptes",
		"manage-project-managers": "Gestion des comptes",
    "manage-marketgroup-managers": "Gestion des comptes",
    "manage-project-export-all-participants": "Exporter tous les participant·e·s",
    "reconciliation-report": "Rapport de réconciliation",
    "subscription-end-report": "Rapport d'abonnement",
    "budget-allowance-report": "Rapport d'enveloppe",
    "cash-register": "Caisses",
    "menu-title-market": "Commerce : {name}",
    "menu-title-market-group": "Groupe de commerces : {name}"
	}
}
</i18n>

<template>
  <div v-if="showSecondaryMenu" class="flex flex-col items-start py-4 border-primary-300 dark:border-grey-900 border-t shrink-0">
    <nav class="space-y-0.5 px-2 w-full" aria-labelledby="secondaryMenuTitle">
      <span
        v-if="userType === USER_TYPE_PROJECTMANAGER && allProjects && allProjects.length > 0"
        id="secondaryMenuTitle"
        class="inline-block mb-1 px-2 font-semibold text-p4 uppercase"
        >{{ t("menu-title-program", { name: allProjects[0].name }) }}</span
      >
      <span
        v-if="userType === USER_TYPE_MARKETGROUPMANAGER && marketGroups && marketGroups.length > 0"
        id="secondaryMenuTitle"
        class="inline-block mb-1 px-2 font-semibold text-p4 uppercase"
        >{{ t("menu-title-market-group", { name: marketGroups[0].name }) }}</span
      >
      <span
        v-if="userType === USER_TYPE_MERCHANT && markets && markets.length > 0"
        id="secondaryMenuTitle"
        class="inline-block mb-1 px-2 font-semibold text-p4 uppercase"
        >{{ t("menu-title-market", { name: markets[0].name }) }}</span
      >
      <SecondaryMenuItem
        v-if="manageProgram"
        :router-link="{ name: $consts.urls.URL_PROJECT_SETTINGS }"
        :label="t('program-settings')" />
      <SecondaryMenuItem
        v-if="manageOrganizationManagers"
        :router-link="{ name: $consts.urls.URL_ORGANIZATION_MANAGER_ADMIN }"
        :label="t('manage-organization-managers')" />
      <SecondaryMenuItem
        v-if="manageProjectManagers"
        :router-link="{ name: $consts.urls.URL_PROJECT_MANAGER_ADMIN }"
        :label="t('manage-project-managers')" />
      <SecondaryMenuItem
        v-if="manageMarketGroupManagers"
        :router-link="{ name: $consts.urls.URL_MARKETGROUP_MANAGER_ADMIN }"
        :label="t('manage-marketgroup-managers')" />
      <SecondaryMenuItem
        v-if="manageProjectManagers || manageSpecificMarketGroup"
        :router-link="{ name: $consts.urls.URL_RECONCILIATION_REPORT }"
        :label="t('reconciliation-report')" />
      <SecondaryMenuItem
        v-if="manageProjectManagers || manageOrganizationManagers"
        :router-link="{ name: $consts.urls.URL_SUBSCRIPTION_END_REPORT }"
        :label="t('subscription-end-report')" />
      <SecondaryMenuItem
        v-if="manageProjectManagers || manageOrganizationManagers"
        :router-link="{ name: $consts.urls.URL_BUDGET_ALLOWANCE_REPORT }"
        :label="t('budget-allowance-report')" />
      <SecondaryMenuItem
        v-if="manageSpecificMarket"
        :router-link="{ name: $consts.urls.URL_CASH_REGISTER }"
        :label="t('cash-register')"
        :icon="MARKET" />
      <button
        v-if="manageProjectManagers && manageBeneficiaries"
        class="secondary-menu-item secondary-menu-item--is-inactive"
        style="width: 285px"
        @click="onExportReport">
        {{ t("manage-project-export-all-participants") }}
      </button>
    </nav>
  </div>
</template>

<script setup>
import gql from "graphql-tag";
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useQuery, useResult, useApolloClient } from "@vue/apollo-composable";

import { useAuthStore } from "@/lib/store/auth";

import SecondaryMenuItem from "@/components/app/secondary-menu-item";

import {
  GLOBAL_MANAGE_PROJECT_MANAGERS,
  GLOBAL_MANAGE_ORGANIZATION_MANAGERS,
  GLOBAL_MANAGE_CARDS,
  GLOBAL_MANAGE_SPECIFIC_PROJECT,
  GLOBAL_MANAGE_CATEGORIES,
  GLOBAL_MANAGE_PRODUCT_GROUP,
  GLOBAL_MANAGE_BENEFICIARIES,
  GLOBAL_MANAGE_SPECIFIC_MARKET_GROUP,
  GLOBAL_MANAGE_SPECIFIC_MARKET,
  GLOBAL_MANAGE_MARKETGROUP_MANAGERS
} from "@/lib/consts/permissions";
import { LANG_EN } from "@/lib/consts/langs";
import {
  LANGUAGE_FILTER_EN,
  LANGUAGE_FILTER_FR,
  USER_TYPE_MARKETGROUPMANAGER,
  USER_TYPE_PROJECTMANAGER,
  USER_TYPE_MERCHANT
} from "@/lib/consts/enums";

import MARKET from "@/lib/icons/market.json";

const { t, locale } = useI18n();

const { resolveClient } = useApolloClient();
const client = resolveClient();

const { getGlobalPermissions, userType } = storeToRefs(useAuthStore());

const manageSpecificProject = computed(() => {
  return getGlobalPermissions.value.includes(GLOBAL_MANAGE_SPECIFIC_PROJECT);
});

const manageSpecificMarket = computed(() => {
  return getGlobalPermissions.value.includes(GLOBAL_MANAGE_SPECIFIC_MARKET);
});

const manageSpecificMarketGroup = computed(() => {
  return getGlobalPermissions.value.includes(GLOBAL_MANAGE_SPECIFIC_MARKET_GROUP);
});

const manageProjectManagers = computed(() => {
  return getGlobalPermissions.value.includes(GLOBAL_MANAGE_PROJECT_MANAGERS);
});

const manageMarketGroupManagers = computed(() => {
  return getGlobalPermissions.value.includes(GLOBAL_MANAGE_MARKETGROUP_MANAGERS);
});

const manageCards = computed(() => {
  return getGlobalPermissions.value.includes(GLOBAL_MANAGE_CARDS);
});

const manageProgram = computed(() => {
  return (
    getGlobalPermissions.value.includes(GLOBAL_MANAGE_CATEGORIES) ||
    getGlobalPermissions.value.includes(GLOBAL_MANAGE_PRODUCT_GROUP)
  );
});

const manageOrganizationManagers = computed(() => {
  return getGlobalPermissions.value.includes(GLOBAL_MANAGE_ORGANIZATION_MANAGERS);
});

const manageBeneficiaries = computed(() => {
  return getGlobalPermissions.value.includes(GLOBAL_MANAGE_BENEFICIARIES);
});

const showSecondaryMenu = computed(() => {
  const showLink =
    manageProjectManagers.value ||
    manageCards.value ||
    manageOrganizationManagers.value ||
    manageSpecificMarketGroup.value ||
    manageSpecificMarket.value;
  return (
    (manageOrganizationManagers.value ||
      manageSpecificProject.value ||
      manageSpecificMarketGroup.value ||
      manageSpecificMarket.value) &&
    showLink
  );
});

const { result: resultProjects } = useQuery(
  gql`
    query SecondaryMenuProjects {
      allProjects {
        id
        name
      }
    }
  `,
  {},
  { fetchPolicy: "cache-first" }
);
const allProjects = useResult(resultProjects);

const { result: resultMarketGroups } = useQuery(
  gql`
    query SecondaryMenuMarketGroups {
      marketGroups {
        id
        name
      }
    }
  `,
  {},
  { fetchPolicy: "cache-first" }
);
const marketGroups = useResult(resultMarketGroups);

const { result: resultMarkets } = useQuery(
  gql`
    query SecondaryMenuMarkets {
      markets {
        id
        name
      }
    }
  `,
  {},
  { fetchPolicy: "cache-first" }
);
const markets = useResult(resultMarkets);

async function onExportReport() {
  let result = null;
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const project = allProjects.value[0];

  result = await client.query({
    query: gql`
      query ExportBeneficiariesList($projectId: ID!, $timeZoneId: String!, $language: Language!) {
        exportBeneficiariesList(id: $projectId, timeZoneId: $timeZoneId, language: $language)
      }
    `,
    variables: {
      projectId: project.id,
      timeZoneId: timeZone,
      language: locale.value === LANG_EN ? LANGUAGE_FILTER_EN : LANGUAGE_FILTER_FR
    }
  });
  window.open(result.data.exportBeneficiariesList, "_blank");
}
</script>
